import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';

export default function AntiguaItinerary3Days() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqData = [
    {
      q: 'Is 3 days enough in Antigua Guatemala?',
      a: 'Yes, 3 days is the ideal minimum for first-time visitors. You can explore the colonial city center, complete one major volcano hike (Acatenango or Pacaya), visit local markets, and enjoy the restaurant scene without feeling rushed. While you could spend longer, 3 days covers the essential experiences most travelers prioritize.'
    },
    {
      q: 'What is the best 3 day itinerary for Antigua Guatemala?',
      a: 'The best 3-day itinerary includes: Day 1 - Explore colonial Antigua (Santa Catalina Arch, Central Park, ruins, markets). Day 2 - Acatenango overnight volcano hike or Pacaya day hike. Day 3 - Recovery day with coffee tour, Hobbitenango, or relaxed city exploration. This balances adventure with cultural immersion.'
    },
    {
      q: 'Should I hike Acatenango or Pacaya on a 3 day Antigua trip?',
      a: 'For 3 days, choose based on fitness level and priorities. Acatenango (overnight, challenging, 10-12 hours) offers spectacular Fuego eruption views but takes 2 days. Pacaya (day hike, moderate, 5-6 hours) is easier and leaves more time for city exploration. Most first-timers with good fitness choose Acatenango.'
    },
    {
      q: 'How much does a 3 day trip to Antigua Guatemala cost?',
      a: 'Budget travelers spend $150-250 total (hostels, street food, free activities). Mid-range travelers spend $400-600 (boutique hotels, restaurants, tours). Comfort travelers spend $800-1,200+ (luxury hotels, private tours, fine dining). This excludes international flights but includes accommodation, food, activities, and local transport.'
    },
    {
      q: 'Where should I stay in Antigua Guatemala for 3 days?',
      a: 'Stay within 3-4 blocks of Central Park for walkability. North of the park (near Santa Catalina Arch) offers quieter streets. South and east sides have more restaurants and nightlife. Book hotels with volcano views if possible. Avoid staying far from the center—you will waste time walking.'
    },
    {
      q: 'Can I visit Lake Atitlán on a 3 day Antigua itinerary?',
      a: 'Not recommended. Lake Atitlán is 2.5-3 hours each way, making it a 10-12 hour day trip. With only 3 days in Antigua, you will feel rushed and miss key Antigua experiences. Save Lake Atitlán for a longer trip (5-7 days minimum) or a separate visit.'
    },
    {
      q: 'What is the best time of year for a 3 day Antigua trip?',
      a: 'November through April (dry season) offers the best weather and clearest volcano views. December-February is peak season with perfect conditions but higher prices. March-April brings Semana Santa celebrations. May-October (rainy season) has afternoon showers but fewer crowds and 20-30% lower prices.'
    },
    {
      q: 'Is Antigua Guatemala safe for a 3 day visit?',
      a: 'Yes, Antigua is one of Guatemala\'s safest cities for tourists. The colonial center is heavily policed and safe to walk day and night. Standard precautions apply: avoid displaying expensive items, use official taxis at night, and stay in well-lit areas. Volcano hikes are safe with reputable tour companies.'
    },
    {
      q: 'How do I get from Guatemala City airport to Antigua?',
      a: 'Book a private shuttle ($35-50 per person, 1 hour) or shared shuttle ($10-15 per person, 1.5 hours) in advance. Avoid public buses with luggage. Shuttles pick up directly at the airport and drop off at your Antigua hotel. Book through your hotel or reputable companies like Antigua Tours or Guatemala Shuttle.'
    },
    {
      q: 'What should I pack for 3 days in Antigua Guatemala?',
      a: 'Pack layers (mornings/evenings are cool, afternoons warm), comfortable walking shoes, sunscreen, hat, light rain jacket, and a daypack. For volcano hikes: warm jacket, gloves, headlamp, and hiking boots. Antigua is 5,000 feet elevation—temperatures range from 50-75°F year-round.'
    },
    {
      q: 'Can I do a 3 day Antigua itinerary as a weekend trip from the US?',
      a: 'Yes, but it requires efficient planning. Fly Friday evening (arrive 9-11pm), spend Saturday and Sunday in Antigua, fly home Monday morning. You will have 2 full days plus partial Friday/Monday. This works best for East Coast travelers. Book airport transfers and tours in advance to maximize time.'
    },
    {
      q: 'Do I need to speak Spanish for 3 days in Antigua?',
      a: 'No, but basic Spanish helps. Most hotels, restaurants, and tour operators in Antigua speak English. Learning basic phrases (greetings, numbers, "how much") enhances interactions with locals and market vendors. Download Google Translate offline for backup.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Antigua Guatemala 3 Day Itinerary: Complete Guide (2026)",
    "description": "Detailed 3-day Antigua Guatemala itinerary with day-by-day plans, volcano hike options, budget breakdown, and local tips for first-time visitors.",
    "author": {
      "@type": "Organization",
      "name": "La Antigua Guatemala Travel Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "La Antigua Guatemala Travel Guide",
      "logo": {
        "@type": "ImageObject",
        "url": "https://la-antigua-guatemala.com/logo.png"
      }
    },
    "datePublished": "2026-02-20",
    "dateModified": "2026-02-20",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://la-antigua-guatemala.com/antigua-itinerary-3-days"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://la-antigua-guatemala.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Itineraries",
        "item": "https://la-antigua-guatemala.com/antigua-guatemala-itineraries"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "3 Day Itinerary",
        "item": "https://la-antigua-guatemala.com/antigua-itinerary-3-days"
      }
    ]
  };

  const schemas = [articleSchema, breadcrumbSchema, faqSchema];

  return (
    <>
      <SEO 
        title="Antigua Guatemala 3 Day Itinerary: Complete Guide (2026)"
        description="Perfect 3-day Antigua Guatemala itinerary with volcano hikes, colonial tours, budget tips, and day-by-day plans for first-time visitors."
        canonical="https://la-antigua-guatemala.com/antigua-itinerary-3-days"
        keywords="antigua guatemala 3 day itinerary, 3 days in antigua guatemala, antigua guatemala itinerary 3 days, antigua weekend itinerary, how many days in antigua guatemala, things to do in antigua guatemala in 3 days"
        schema={schemas}
        lastModified="2026-02-20"
        ogImage={{
          url: "https://la-antigua-guatemala.com/og/3-day-itinerary.jpg",
          width: "1200",
          height: "630"
        }}
      />
      <Navbar darkText />
      
      <article className="min-h-screen bg-white">
        {/* Hero */}
        <section className="relative h-[55vh] flex items-center justify-center mt-20">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/ca9b64e3ef34e5d7ffcf4e61e16bb790.jpg"
              alt="Antigua Guatemala 3 Day Itinerary"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Antigua Guatemala 3 Day Itinerary</h1>
            <p className="text-xl md:text-2xl mb-4">The perfect first-time visitor guide: colonial charm, volcano adventures, and local culture</p>
            <p className="text-lg opacity-90">Day-by-day plans • Budget breakdown • Pro tips from locals</p>
          </div>
        </section>

        {/* Breadcrumbs */}
        <nav className="bg-gray-50 py-3 px-4">
          <div className="max-w-5xl mx-auto">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link to="/" className="text-gray-600 hover:text-teal-500 transition">
                  Home
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-gray-400">/</span>
                <Link to="/antigua-guatemala-itineraries" className="text-gray-600 hover:text-teal-500 transition">
                  Itineraries
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-gray-400">/</span>
                <span className="text-gray-900 font-medium">3 Day Itinerary</span>
              </li>
            </ol>
          </div>
        </nav>

        {/* Quick Take Box */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="bg-teal-50 border-l-4 border-teal-500 p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Take: Is This Itinerary Right for You?</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-user-line text-teal-500 mr-2"></i>
                    Ideal For
                  </h3>
                  <p className="text-gray-700 text-sm">First-time visitors, couples, solo travelers, US long weekends, adventure seekers with limited time</p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-speed-line text-teal-500 mr-2"></i>
                    Trip Pace
                  </h3>
                  <p className="text-gray-700 text-sm">Moderate to active. One challenging day (volcano hike), two relaxed days exploring the city and culture</p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <i className="ri-star-line text-teal-500 mr-2"></i>
                    Key Highlights
                  </h3>
                  <p className="text-gray-700 text-sm">Colonial architecture, volcano hike with eruption views, local markets, rooftop dining, coffee culture</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Three days in Antigua Guatemala is the sweet spot for first-time visitors. It's enough time to explore the colonial city center, complete one major volcano hike, experience local markets and culture, and still have breathing room to enjoy the restaurants and cafes that make Antigua special.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This <strong>Antigua Guatemala 3 day itinerary</strong> is designed for travelers who want to maximize their time without feeling rushed. We've tested this exact schedule with hundreds of US visitors, and it consistently delivers the right balance of adventure, culture, and relaxation.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                You'll spend Day 1 getting oriented and exploring the colonial center. Day 2 brings the highlight: a volcano hike with views of erupting Fuego. Day 3 is your recovery and exploration day—choose between a coffee tour, Hobbitenango adventure park, or relaxed city wandering. By the end, you'll understand why travelers consistently rank Antigua as Guatemala's must-visit destination.
              </p>
            </div>
          </div>
        </section>

        {/* Why 3 Days */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Why 3 Days in Antigua Is Perfect</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                After helping hundreds of travelers plan their <strong>Antigua Guatemala itinerary</strong>, we've found that 3 days hits the ideal balance. Here's why:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Can Complete One Major Volcano Hike</h3>
                  <p className="text-gray-700">
                    The Acatenango overnight hike (our top recommendation) takes 2 days but only uses one full day of your itinerary. You depart Day 2 morning and return Day 3 morning, leaving Day 3 afternoon free. Alternatively, the Pacaya day hike takes just 5-6 hours, leaving your entire Day 3 open.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Won't Feel Rushed</h3>
                  <p className="text-gray-700">
                    Two days forces you to skip either the volcano hike or proper city exploration. Four or five days can feel too long if you're not adding day trips to Lake Atitlán or Chichicastenango. Three days lets you experience Antigua's highlights at a comfortable pace.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">It Works for US Long Weekends</h3>
                  <p className="text-gray-700">
                    Fly Friday evening (arrive 9-11pm), spend Saturday and Sunday in Antigua, fly home Monday morning. You get 2 full days plus partial Friday/Monday—enough for the essential experiences. This makes Antigua one of the best quick international getaways from the US.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You'll Actually Relax</h3>
                  <p className="text-gray-700">
                    Three days includes built-in recovery time after your volcano hike. You're not constantly packing, moving, or rushing to the next activity. You'll have time to sit at a cafe, browse markets, and enjoy Antigua's laid-back atmosphere.
                  </p>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                That said, if you have more time, consider our <Link to="/antigua-guatemala-2-week-itinerary" className="text-teal-500 hover:underline font-semibold">2-week Antigua itinerary</Link> or check out other <Link to="/antigua-guatemala-itineraries" className="text-teal-500 hover:underline font-semibold">Antigua itinerary options</Link> for different trip lengths.
              </p>
            </div>
          </div>
        </section>

        {/* Where to Stay */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Where to Stay in Antigua for 3 Days</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Location matters in Antigua. Stay within 3-4 blocks of Central Park (Parque Central) to maximize walkability. The colonial center is compact—about 10 blocks by 10 blocks—so you can walk everywhere.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <p className="text-lg font-semibold text-gray-900 mb-2">Pro Tip: Book Hotels with Volcano Views</p>
                <p className="text-gray-700">
                  Many hotels have rooftop terraces with views of Agua, Fuego, and Acatenango volcanoes. It's worth paying $10-20 extra per night for this feature—you'll spend mornings and evenings on that terrace.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Best Areas to Stay</h3>

              <div className="space-y-6 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">North of Central Park (Near Santa Catalina Arch)</h4>
                  <p className="text-gray-700 mb-2">
                    Quieter streets, fewer tourists, excellent volcano views. Close to the arch and main attractions but away from nightlife noise. Best for couples and travelers who prioritize peace.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Budget:</strong> $25-40/night • <strong>Mid-range:</strong> $60-100/night • <strong>Luxury:</strong> $150-300/night
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">South and East of Central Park</h4>
                  <p className="text-gray-700 mb-2">
                    More restaurants, bars, and nightlife. Slightly more tourist activity but still safe and walkable. Best for solo travelers and groups who want easy access to dining and socializing.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Budget:</strong> $20-35/night • <strong>Mid-range:</strong> $50-90/night • <strong>Luxury:</strong> $120-250/night
                  </p>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                For detailed hotel recommendations by budget and style, see our complete <Link to="/best-hotels-in-antigua-guatemala" className="text-teal-500 hover:underline font-semibold">guide to the best hotels in Antigua Guatemala</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Getting There */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Getting to Antigua from Guatemala City Airport</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Guatemala City's La Aurora International Airport (GUA) is 45 minutes to 1 hour from Antigua, depending on traffic. Book your transfer in advance—do not take public buses with luggage.
              </p>

              <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Transfer Options</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-bold text-gray-900">Private Shuttle (Recommended)</p>
                    <p className="text-gray-700 text-sm">$35-50 per person • 1 hour • Door-to-door service • Book through your hotel or Antigua Tours</p>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Shared Shuttle</p>
                    <p className="text-gray-700 text-sm">$10-15 per person • 1.5 hours • Waits for other passengers • Budget-friendly option</p>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Private Car</p>
                    <p className="text-gray-700 text-sm">$60-80 total • 1 hour • Best for groups of 3-4 • Most comfortable option</p>
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                For complete details on all transfer options, timing, and booking tips, read our full <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:underline font-semibold">Guatemala City airport to Antigua transfer guide</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Day 1 */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                1
              </div>
              <h2 className="text-4xl font-bold text-gray-900 ml-4">Day 1: Arrival &amp; Colonial Antigua Exploration</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Morning (9:00 AM - 12:00 PM)</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Start with breakfast at your hotel or a nearby cafe. Try Cafe Sky or Fernando's Kaffee for excellent coffee and volcano views. Spend 30-45 minutes planning your day and getting oriented.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Walk to Central Park (Parque Central) and spend time observing local life. The park is Antigua's social hub—you'll see families, vendors, and tourists mixing. Visit the Cathedral of San José on the east side (free entry, donations appreciated).
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Walk north on 5a Avenida Norte to Santa Catalina Arch, Antigua's most photographed landmark. The yellow arch frames Agua volcano perfectly. Arrive before 10:30 AM to avoid crowds. Continue north to La Merced Church (white and yellow baroque facade—stunning).
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <p className="text-lg font-semibold text-gray-900 mb-2">Pro Tip: Book Your Volcano Hike Today</p>
                <p className="text-gray-700">
                  Stop by a tour agency this morning to book tomorrow's volcano hike. Most agencies are on 5a Avenida Norte or 4a Calle Oriente. Booking in person often gets you better prices than online ($35-45 for Pacaya, $65-85 for Acatenango).
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Afternoon (12:00 PM - 5:00 PM)</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Lunch at a local restaurant. Try Rincon Tipico for traditional Guatemalan food ($8-12) or Hector's Bistro for international cuisine ($12-18). Avoid tourist traps on Central Park—walk one block away for better value.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Visit the ruins of Convento Santa Clara or Iglesia y Convento de la Recolección ($5 entry each). These colonial ruins show Antigua's earthquake history and offer excellent photo opportunities. Spend 45-60 minutes at each.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Explore the Mercado de Artesanías (handicraft market) near the bus terminal. This is where locals shop—prices are 30-50% lower than tourist shops. Practice your Spanish and bargain respectfully. Budget 1-2 hours for browsing.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Walk through the Tanque La Unión neighborhood (northeast of Central Park) to see residential Antigua. Colorful houses, quiet streets, and local life away from tourist areas.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Evening (5:00 PM - 9:00 PM)</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Return to your hotel to rest and freshen up. Antigua's elevation (5,000 feet) can be tiring on Day 1.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Sunset at a rooftop bar. Cafe Sky (north side of Central Park) offers 360-degree volcano views. Arrive by 5:30 PM to secure a table. Order a michelada or local beer ($3-5) and watch the volcanoes glow.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Dinner at a mid-range restaurant. Try Tartines for French-Guatemalan fusion ($15-25), Sobremesa for farm-to-table ($18-28), or Nokiate for upscale Guatemalan ($20-35). Make reservations for popular spots.
              </p>

              <div className="bg-white border border-gray-200 p-6 rounded-lg mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 1 Logistics</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Walking:</strong> 4-6 miles total (wear comfortable shoes)</li>
                  <li><strong>Transport:</strong> None needed (everything is walkable)</li>
                  <li><strong>Bookings:</strong> Reserve volcano hike for Day 2, dinner reservations</li>
                  <li><strong>Timing:</strong> Flexible—adjust based on energy levels</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 1 Budget Estimate</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Budget Traveler</span>
                    <span className="text-gray-700">$30-45</span>
                  </div>
                  <p className="text-sm text-gray-600">Street food, free activities, local markets, hostel breakfast</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-gray-900">Mid-Range Traveler</span>
                    <span className="text-gray-700">$80-120</span>
                  </div>
                  <p className="text-sm text-gray-600">Restaurant meals, ruin entries, rooftop drinks, souvenirs</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-gray-900">Comfort Traveler</span>
                    <span className="text-gray-700">$150-200</span>
                  </div>
                  <p className="text-sm text-gray-600">Fine dining, private tours, premium experiences, shopping</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Day 2 */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                2
              </div>
              <h2 className="text-4xl font-bold text-gray-900 ml-4">Day 2: Volcano Hike (Acatenango or Pacaya)</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                This is your adventure day. You have two excellent options: the challenging Acatenango overnight hike or the easier Pacaya day hike. Both offer spectacular volcano experiences, but they're very different.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option A: Acatenango Overnight Hike (Recommended)</h3>

              <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Why Choose Acatenango</h4>
                <p className="text-gray-700 mb-4">
                  Acatenango is Guatemala's most spectacular hike. You camp at 12,300 feet with direct views of Fuego volcano erupting every 15-20 minutes. Watching lava explosions at sunset and sunrise is unforgettable. This is the experience most travelers prioritize.
                </p>
                <p className="text-gray-700 font-semibold">
                  Requirements: Good fitness level, comfortable with cold temperatures (20-30°F at night), able to hike 5-6 hours uphill
                </p>
              </div>

              <h4 className="text-xl font-bold text-gray-900 mb-4">Acatenango Day-by-Day</h4>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Day 2 (Departure Day):</strong> Pickup at your hotel 8:00-9:00 AM. Drive 1.5 hours to trailhead. Start hiking 10:30-11:00 AM. Reach base camp 3:00-4:00 PM. Watch Fuego erupt at sunset. Dinner provided. Sleep in tents (sleeping bags and gear included). Wake at 4:00 AM.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Day 3 (Summit Day):</strong> Hike to Acatenango summit (13,045 feet) for sunrise 4:30-6:30 AM. Return to base camp for breakfast. Descend to trailhead 8:00 AM-12:00 PM. Return to Antigua 1:00-2:00 PM. Rest of Day 3 is yours.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <p className="text-lg font-semibold text-gray-900 mb-2">Pro Tip: Pack Smart for Acatenango</p>
                <p className="text-gray-700 mb-3">
                  Bring: warm jacket, gloves, hat, headlamp, 3L water, snacks, sunscreen, hiking boots. Tour provides: tent, sleeping bag, meals, guide. Rent gear in Antigua if needed ($5-10 per item).
                </p>
                <p className="text-gray-700">
                  Read our complete <Link to="/acatenango-hike-from-antigua" className="text-teal-500 hover:underline font-semibold">Acatenango hike guide</Link> for detailed packing lists and preparation tips.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option B: Pacaya Volcano Day Hike</h3>

              <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Why Choose Pacaya</h4>
                <p className="text-gray-700 mb-4">
                  Pacaya is an active volcano where you can see lava flows and roast marshmallows over volcanic heat. The hike is shorter (5-6 hours total) and less challenging than Acatenango. You return to Antigua the same day, leaving your entire Day 3 free for other activities.
                </p>
                <p className="text-gray-700 font-semibold">
                  Requirements: Moderate fitness, comfortable with 2-3 hours uphill hiking, no overnight camping
                </p>
              </div>

              <h4 className="text-xl font-bold text-gray-900 mb-4">Pacaya Schedule</h4>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Morning Departure:</strong> Pickup 6:00-7:00 AM. Drive 1.5 hours to trailhead. Hike 1.5-2 hours to active lava zone. Spend 1 hour at the top (roast marshmallows, explore lava fields). Descend 1-1.5 hours. Return to Antigua 1:00-2:00 PM.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Afternoon Option:</strong> Some tours depart 2:00-3:00 PM for sunset hikes. You'll see lava glowing at dusk—spectacular but less dramatic than Acatenango's Fuego eruptions.
              </p>

              <div className="bg-white border border-gray-200 p-6 rounded-lg mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 2 Logistics</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Acatenango:</strong> $65-85 per person (includes transport, guide, meals, gear)</li>
                  <li><strong>Pacaya:</strong> $35-45 per person (includes transport, guide, park entry)</li>
                  <li><strong>Booking:</strong> Book Day 1 in Antigua or 2-3 days in advance online</li>
                  <li><strong>Weather:</strong> Dry season (Nov-Apr) has best conditions</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 2 Budget Estimate</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Pacaya Hike</span>
                    <span className="text-gray-700">$40-60</span>
                  </div>
                  <p className="text-sm text-gray-600">Tour cost, snacks, tips for guide</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-gray-900">Acatenango Hike</span>
                    <span className="text-gray-700">$70-100</span>
                  </div>
                  <p className="text-sm text-gray-600">Tour cost, gear rental (if needed), tips for guides</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Day 3 */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                3
              </div>
              <h2 className="text-4xl font-bold text-gray-900 ml-4">Day 3: Recovery &amp; Choose Your Adventure</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                If you hiked Acatenango, you'll return to Antigua around 1:00-2:00 PM. If you hiked Pacaya yesterday, you have the full day free. Either way, Day 3 is your recovery and exploration day. Choose activities based on your energy level and interests.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option A: Coffee Farm Tour (Recommended)</h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Guatemala produces some of the world's best coffee, and Antigua is surrounded by coffee farms. A half-day tour (3-4 hours) teaches you the full process from plant to cup. You'll walk through plantations, see processing facilities, and taste multiple varieties.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Best farms:</strong> Finca Filadelfia ($25-35, includes transport), Cafetalera La Azotea ($20-30), or ChocoMuseo for combined chocolate and coffee tour ($35-45). Book through your hotel or tour agencies on 5a Avenida Norte.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option B: Hobbitenango Adventure Park</h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                This Lord of the Rings-themed park sits in the mountains above Antigua with incredible valley views. Activities include zip lines, giant hand sculpture (Instagram-famous), hiking trails, and hobbit houses. It's quirky, fun, and perfect for a relaxed day.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Details:</strong> $10 entry + $15-25 for activities. Tuk-tuk from Antigua costs $8-10 each way (30 minutes). Go in the afternoon (2:00-6:00 PM) for best light and fewer crowds.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option C: Relaxed City Day</h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                If you're tired from Acatenango, spend Day 3 recovering in Antigua. Sleep in, have a long breakfast, get a massage ($25-40 for 60 minutes), browse bookstores and galleries, and enjoy slow cafe time.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Visit Cerro de la Cruz (hill with cross) for panoramic city views—it's a 20-minute uphill walk from Central Park. Go with a group or organized tour for safety (occasional robberies reported on this trail).
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">Option D: Chichicastenango Market (Thursday or Sunday Only)</h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                If Day 3 falls on Thursday or Sunday, consider visiting Chichicastenango market—Guatemala's largest indigenous market. It's 2 hours from Antigua, so you'll need a full day (7:00 AM-5:00 PM).
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Details:</strong> Tours cost $35-50 including transport and guide. The market is massive—textiles, handicrafts, food, and local culture. Arrive early (before 10:00 AM) to avoid crowds.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <p className="text-lg font-semibold text-gray-900 mb-2">Pro Tip: Save Energy for Your Last Evening</p>
                <p className="text-gray-700">
                  Don't over-schedule Day 3. You'll want energy for a nice farewell dinner and evening walk through Antigua. The city is magical at night with lit colonial buildings and volcano silhouettes.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Evening: Farewell Dinner</h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Celebrate your last night with a special dinner. Splurge on one of Antigua's top restaurants: Angeline (French fine dining, $40-60), Mesón Panza Verde (romantic courtyard, $35-50), or Casa Santo Domingo (historic hotel restaurant, $30-45). Make reservations.
              </p>

              <div className="bg-white border border-gray-200 p-6 rounded-lg mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 3 Logistics</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Timing:</strong> Flexible based on Acatenango return time</li>
                  <li><strong>Transport:</strong> Tuk-tuks for Hobbitenango, tours include transport</li>
                  <li><strong>Bookings:</strong> Coffee tours and Chichi market need advance booking</li>
                  <li><strong>Energy:</strong> Listen to your body—rest if needed</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Day 3 Budget Estimate</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Budget Traveler</span>
                    <span className="text-gray-700">$25-40</span>
                  </div>
                  <p className="text-sm text-gray-600">Relaxed city day, free activities, casual meals</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-gray-900">Mid-Range Traveler</span>
                    <span className="text-gray-700">$70-100</span>
                  </div>
                  <p className="text-sm text-gray-600">Coffee tour or Hobbitenango, nice dinner, massage</p>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold text-gray-900">Comfort Traveler</span>
                    <span className="text-gray-700">$120-180</span>
                  </div>
                  <p className="text-sm text-gray-600">Multiple activities, fine dining, spa treatments, shopping</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Day 3 Activity Comparison</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead className="bg-teal-500 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold">Activity</th>
                    <th className="px-6 py-4 text-left font-bold">Duration</th>
                    <th className="px-6 py-4 text-left font-bold">Cost</th>
                    <th className="px-6 py-4 text-left font-bold">Energy Level</th>
                    <th className="px-6 py-4 text-left font-bold">Best For</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Coffee Farm Tour</td>
                    <td className="px-6 py-4 text-gray-700">3-4 hours</td>
                    <td className="px-6 py-4 text-gray-700">$20-45</td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">Low</span></td>
                    <td className="px-6 py-4 text-gray-700">Coffee lovers, cultural learners</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Hobbitenango</td>
                    <td className="px-6 py-4 text-gray-700">4-5 hours</td>
                    <td className="px-6 py-4 text-gray-700">$35-50</td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">Moderate</span></td>
                    <td className="px-6 py-4 text-gray-700">Adventure seekers, photographers</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Relaxed City Day</td>
                    <td className="px-6 py-4 text-gray-700">Full day</td>
                    <td className="px-6 py-4 text-gray-700">$20-60</td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">Very Low</span></td>
                    <td className="px-6 py-4 text-gray-700">Recovery after Acatenango</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">Chichicastenango</td>
                    <td className="px-6 py-4 text-gray-700">Full day</td>
                    <td className="px-6 py-4 text-gray-700">$35-70</td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">Moderate</span></td>
                    <td className="px-6 py-4 text-gray-700">Thu/Sun only, culture enthusiasts</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Total Budget */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">3-Day Antigua Budget Breakdown</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Here's what you can expect to spend for 3 days in Antigua Guatemala, excluding international flights. These estimates include accommodation, food, activities, and local transport.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Budget Traveler</h3>
                  <p className="text-4xl font-bold text-teal-500 mb-4">$150-250</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Hostel dorms ($8-15/night)</li>
                    <li>• Street food &amp; local eateries</li>
                    <li>• Pacaya volcano hike</li>
                    <li>• Free walking tours</li>
                    <li>• Shared shuttles</li>
                    <li>• Minimal shopping</li>
                  </ul>
                </div>

                <div className="bg-teal-50 p-6 rounded-lg border-2 border-teal-500">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Mid-Range Traveler</h3>
                  <p className="text-4xl font-bold text-teal-500 mb-4">$400-600</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Boutique hotels ($50-90/night)</li>
                    <li>• Restaurant meals</li>
                    <li>• Acatenango overnight hike</li>
                    <li>• Coffee farm tour</li>
                    <li>• Private shuttles</li>
                    <li>• Souvenirs &amp; shopping</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Comfort Traveler</h3>
                  <p className="text-4xl font-bold text-teal-500 mb-4">$800-1,200+</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Luxury hotels ($150-300/night)</li>
                    <li>• Fine dining experiences</li>
                    <li>• Private volcano tours</li>
                    <li>• Multiple activities &amp; tours</li>
                    <li>• Private car service</li>
                    <li>• Spa treatments &amp; shopping</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Costs to Consider</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Airport Transfer:</strong> $10-50 each way (shared to private)</li>
                  <li><strong>Travel Insurance:</strong> $15-30 for 3 days (highly recommended)</li>
                  <li><strong>Tips:</strong> 10% restaurants, $5-10 per day for tour guides</li>
                  <li><strong>SIM Card:</strong> $10-15 for local data (optional)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Common Mistakes to Avoid on a 3-Day Antigua Trip</h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">1. Not Booking Volcano Hikes in Advance</h3>
                <p className="text-gray-700">
                  During peak season (December-March), Acatenango tours sell out 2-3 days ahead. Book online before arrival or immediately upon reaching Antigua on Day 1. Don't wait until Day 2 morning—you'll be disappointed.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">2. Underestimating Acatenango's Difficulty</h3>
                <p className="text-gray-700">
                  This is a challenging hike at high altitude. If you're not regularly active or have altitude concerns, choose Pacaya instead. There's no shame in picking the easier option—Pacaya is still spectacular and you'll enjoy it more than struggling on Acatenango.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">3. Trying to Visit Lake Atitlán</h3>
                <p className="text-gray-700">
                  With only 3 days, skip Lake Atitlán. The 5-6 hours of total travel time eats your entire Day 3, and you'll barely see the lake. Focus on Antigua and save Atitlán for a longer trip. Quality over quantity.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">4. Overpacking Your Schedule</h3>
                <p className="text-gray-700">
                  Three days goes fast, especially with a volcano hike taking one full day. Don't try to cram in every activity. Pick 2-3 priorities and leave buffer time. Antigua rewards slow exploration—sitting in cafes, wandering streets, and soaking in the atmosphere.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">5. Staying Too Far from Central Park</h3>
                <p className="text-gray-700">
                  Hotels more than 5 blocks from Central Park waste your limited time walking. Pay slightly more to stay central—the convenience is worth it for a short trip. You'll walk back to your hotel multiple times per day.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">6. Not Bringing Warm Clothes</h3>
                <p className="text-gray-700">
                  Antigua is 5,000 feet elevation—mornings and evenings are cool (50-60°F) even in summer. Volcano hikes are even colder. Pack layers, a warm jacket, and closed-toe shoes. You can't buy quality gear in Antigua at reasonable prices.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">7. Skipping Travel Insurance</h3>
                <p className="text-gray-700">
                  Volcano hikes carry injury risk, and Guatemala's medical system requires upfront payment. Travel insurance costs $15-30 for 3 days and covers medical emergencies, trip cancellations, and lost luggage. Don't skip it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Is Antigua Guatemala Safe for 3 Days?</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Yes, Antigua is one of Guatemala's safest cities for tourists. The colonial center is heavily policed, and violent crime against tourists is rare. That said, petty theft (pickpocketing, bag snatching) does occur, especially in crowded areas.
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Safety Tips for Your 3-Day Visit</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Keep valuables in your hotel safe (passport, extra cash, electronics)</li>
                  <li>• Use official taxis or Uber at night (don't walk alone after 10 PM)</li>
                  <li>• Avoid displaying expensive cameras, jewelry, or phones</li>
                  <li>• Don't hike Cerro de la Cruz alone—go with a group or tour</li>
                  <li>• Book volcano hikes with reputable companies (ask your hotel)</li>
                  <li>• Keep copies of important documents (passport, insurance)</li>
                  <li>• Trust your instincts—if something feels off, leave</li>
                </ul>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The vast majority of visitors have zero safety issues in Antigua. Use common sense, stay aware of your surroundings, and you'll be fine. The city feels safer than many US cities.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                For detailed safety information, read our complete guide: <Link to="/is-antigua-guatemala-safe" className="text-teal-500 hover:underline font-semibold">Is Antigua Guatemala Safe?</Link>
              </p>
            </div>
          </div>
        </section>

        {/* What to Pack */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">What to Pack for 3 Days in Antigua Guatemala</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Essential Items</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Comfortable walking shoes (you'll walk 4-6 miles daily)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Layers: t-shirts, long sleeves, warm jacket</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Light rain jacket (even in dry season)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Sunscreen (SPF 30+) and hat</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Daypack (15-20L) for daily excursions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Reusable water bottle (refill at hotels)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Power adapter (Guatemala uses US plugs)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Basic first aid kit and medications</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Volcano Hikes</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Hiking boots or sturdy trail shoes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Warm jacket (20-30°F at night on Acatenango)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Gloves and warm hat</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Headlamp with extra batteries</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>3L water capacity (bottles or hydration pack)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>High-energy snacks (trail mix, bars, chocolate)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Trekking poles (optional but helpful)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Camera with extra batteries (cold drains power)</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What NOT to Bring</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Expensive jewelry or watches (leave at home)</li>
                <li>• Too many clothes (you can do laundry for $5-8)</li>
                <li>• Large suitcases (cobblestone streets are rough on wheels)</li>
                <li>• Shorts and tank tops (locals dress modestly)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Need Help Planning Your 3-Day Antigua Trip?</h2>
            <p className="text-xl text-white/90 mb-8">Get personalized recommendations based on your travel dates, interests, and budget. We'll help you create the perfect itinerary.</p>
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
              Get Free Itinerary
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition cursor-pointer"
                  >
                    <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.q}</h3>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-teal-500 flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Related Antigua Guides</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/antigua-guatemala-travel-guide" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-map-2-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Complete Travel Guide</h3>
                <p className="text-gray-600 text-sm">Everything you need to know about visiting Antigua Guatemala</p>
              </Link>

              <Link to="/acatenango-hike-from-antigua" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-mountain-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Acatenango Hike Guide</h3>
                <p className="text-gray-600 text-sm">Complete guide to the overnight volcano hike with packing list</p>
              </Link>

              <Link to="/best-hotels-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-hotel-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Best Hotels in Antigua</h3>
                <p className="text-gray-600 text-sm">Top accommodation recommendations by budget and location</p>
              </Link>

              <Link to="/things-to-do-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-compass-3-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Things to Do</h3>
                <p className="text-gray-600 text-sm">Top activities, attractions, and experiences in Antigua</p>
              </Link>

              <Link to="/day-trips-from-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-road-map-line"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Day Trips from Antigua</h3>
                <p className="text-gray-600 text-sm">Best day trips including Lake Atitlán and Chichicastenango</p>
              </Link>

              <Link to="/antigua-guatemala-itineraries" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                  <i className="ri-calendar-line text-teal-500"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Other Itineraries</h3>
                <p className="text-gray-600 text-sm">2-day and 5-day options</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Experience Antigua Guatemala?</h2>
            <p className="text-xl text-white/90 mb-8">Download our free PDF itinerary with day-by-day plans, maps, restaurant recommendations, and insider tips. Plus, get personalized help planning your perfect 3-day trip.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
                Get Free Itinerary
              </Link>
              <Link to="/plan-my-antigua-trip" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap border-2 border-white">
                Plan My Custom Trip
              </Link>
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </>
  );
}
